import { Badge, Button, Flex, IconButton, Tab, TabList, Tabs, useDisclosure } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Icon } from '../../../components/Icon'
import { PageTitle } from '../../../components/PageTitle'
import { Tooltip } from '../../../components/Tooltip'
import { useHasRole, useAppToast } from '../../../helpers'
import { IScheduleFormOptions, TScheduleTerm } from '../../../services/ScheduleClasses/interfaces'
import ScheduleClassService from '../../../services/ScheduleClasses/ScheduleClassService'
import { currentTermIndex, TERM_LABELS, TERMS } from './constants'
import CoursePagesScheduleSwitch from './CoursePagesScheduleSwitch'
import CourseThemeColorsModal from './CourseThemeColorsModal'
import ScheduleClassesTable from './ScheduleClassesTable'
import YearSelectModal from './YearSelectModal'

const EMPTY_COUNTS: Record<TScheduleTerm, number> = {
  january_break: 0,
  first_semester: 0,
  july_break: 0,
  second_semester: 0
}

/**
 * Cronograma de Turmas (ABRA-181).
 *
 * A tela é sempre escopada a um ano, escolhido num modal bloqueante na entrada, e a um
 * dos 4 períodos fixos do ano, escolhido pelas abas.
 *
 * As abas renderizam só a TabList: uma única ScheduleClassesTable fica abaixo recebendo o
 * período por prop. Com TabPanels haveria quatro instâncias, cada uma com o próprio debounce
 * e a própria paginação, e trocar de aba perderia os filtros.
 */
export default function ScheduleClassesPage() {
  const toast = useAppToast()
  const hasRole = useHasRole()
  const service = useMemo(() => new ScheduleClassService(), [])
  const yearModal = useDisclosure({ defaultIsOpen: true })
  const colorsModal = useDisclosure()

  const [year, setYear] = useState<number | null>(null)
  const [years, setYears] = useState<number[]>([])
  const [tabIndex, setTabIndex] = useState(currentTermIndex())
  const [counts, setCounts] = useState<Record<TScheduleTerm, number>>(EMPTY_COUNTS)
  const [options, setOptions] = useState<IScheduleFormOptions>({ courses: [], teachers: [] })

  const term = TERMS[tabIndex]
  const canEditSchedule = hasRole('edit_class_schedule')

  useEffect(() => {
    const fallbackYear = new Date().getFullYear()

    service
      .years()
      .then(setYears)
      .catch(() => {
        setYears([fallbackYear, fallbackYear + 1, fallbackYear + 2])
        toast.error('Erro ao carregar os anos disponíveis')
      })

    service
      .formOptions()
      .then(setOptions)
      .catch(() => toast.error('Erro ao carregar cursos e professores'))
  }, [service, toast])

  const refreshCounts = useCallback(async () => {
    if (year === null) return

    try {
      const response = await service.termCounts(year)
      setCounts(response.counts)
    } catch {
      toast.error('Erro ao carregar os contadores dos períodos')
    }
  }, [service, year, toast])

  useEffect(() => {
    refreshCounts()
  }, [refreshCounts])

  return (
    <>
      <PageTitle
        title="Cronograma de Turmas"
        mb={4}
        description={
          year !== null && (
            <Flex align="center" gap={2}>
              <span>Ano: {year}</span>
              <Button size="xs" variant="outline" onClick={yearModal.onOpen}>
                Trocar ano
              </Button>
            </Flex>
          )
        }
        actions={
          <Flex align="center" gap={4} wrap="wrap">
            <CoursePagesScheduleSwitch canEdit={canEditSchedule} />
            <Tooltip label="Cores dos cursos">
              <IconButton
                aria-label="Cores dos cursos"
                icon={<Icon name="lni lni-brush" />}
                onClick={colorsModal.onOpen}
                isDisabled={year === null}
              />
            </Tooltip>
          </Flex>
        }
      />

      {year !== null && (
        <>
          <Tabs index={tabIndex} onChange={setTabIndex} mb={4}>
            {/*
              O scroll fica na TabList e não no Tabs, senão a barra aparece por cima da borda
              inferior das abas. E a barra em si é escondida: são 4 abas de rótulo curto, que
              cabem em qualquer tela de trabalho — o scroll existe só como saída no celular,
              onde a barra nativa nem ocupa espaço.
            */}
            <TabList
              overflowX="auto"
              overflowY="hidden"
              sx={{
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {TERMS.map((t) => (
                <Tab key={t} whiteSpace="nowrap">
                  {TERM_LABELS[t]}
                  <Badge ml={2}>{counts[t]}</Badge>
                </Tab>
              ))}
            </TabList>
          </Tabs>

          <ScheduleClassesTable
            year={year}
            term={term}
            options={options}
            onMutate={refreshCounts}
            onGoToPeriod={(nextTerm, nextYear) => {
              // A data da turma (ou da planilha) manda no ano e no período: seguir a tela para
              // eles é o que evita o resumo de sucesso ao lado de uma tabela vazia.
              if (nextYear !== null) {
                setYear(nextYear)
                // A lista de anos veio na montagem da tela: um ano que acabou de nascer aqui não
                // estaria nela, e "Trocar ano" não conseguiria voltar para ele.
                setYears((current) =>
                  current.includes(nextYear) ? current : [...current, nextYear].sort((a, b) => b - a)
                )
              }
              if (nextTerm !== null) setTabIndex(TERMS.indexOf(nextTerm))
            }}
          />
        </>
      )}

      <YearSelectModal
        isOpen={yearModal.isOpen}
        years={years}
        currentYear={year}
        onSelect={(selected) => {
          setYear(selected)
          yearModal.onClose()
        }}
        onClose={yearModal.onClose}
      />

      <CourseThemeColorsModal isOpen={colorsModal.isOpen} onClose={colorsModal.onClose} canEdit={canEditSchedule} />
    </>
  )
}
